import React from 'react'
import PropTypes from 'prop-types'
import { format, parseISO, isSameDay } from 'date-fns'
import { Text } from 'shared/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useHistory } from 'react-router-dom'

import { GameCard } from '../view/components/tournamentDetailsPanel/components/tournamentGamesTab/components/gameCard'
import * as styles from './styles'

const TeamScheduleComponent = ({
	teamId,
	team,
	loading,
	error,
	teamGames,
	gamesLoading,
	gamesError
}) => {
	const history = useHistory()

	const handleBack = () => {
		history.goBack()
	}

	const getTeamAbbreviation = (teamName, teamObj) => {
		if (teamObj?.teamName) {
			// Generate 3-letter abbreviation from team name
			const words = teamObj.teamName.split(' ')
			if (words.length >= 2) {
				return words.slice(0, 2).map(word => word.charAt(0)).join('') +
					(words[0].length > 1 ? words[0].charAt(1).toUpperCase() : '')
			}
			return teamObj.teamName.substring(0, 3).toUpperCase()
		}

		if (teamName) {
			return teamName.substring(0, 3).toUpperCase()
		}

		return 'TBD'
	}

	const getGamesByDate = () => {
		if (!teamGames || teamGames.length === 0) return {}

		return teamGames.reduce((acc, game) => {
			if (!game.startTime) return acc

			const gameDate = parseISO(game.startTime)
			const dateKey = format(gameDate, 'yyyy-MM-dd')

			if (!acc[dateKey]) {
				acc[dateKey] = {
					date: gameDate,
					games: []
				}
			}

			acc[dateKey].games.push(game)
			return acc
		}, {})
	}

	if (loading || gamesLoading) {
		return (
			<div css={styles.container}>
				<div css={styles.header}>
					<FontAwesomeIcon
						icon="arrow-left"
						css={styles.backArrow}
						onClick={handleBack}
					/>
					<Text size={16} bold css={styles.headerTitle}>
						{team?.teamName ? `${getTeamAbbreviation(team?.teamName, team)}: Schedule` : 'Loading Schedule...'}
					</Text>
				</div>
				<div css={styles.loadingContainer}>
					<Text size={14} css={styles.loadingText}>
						Loading schedule...
					</Text>
				</div>
			</div>
		)
	}

	if (error || gamesError) {
		const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error'
		const gamesErrorMessage = typeof gamesError === 'string' ? gamesError : gamesError?.message || 'Unknown error'
		const displayError = error ? errorMessage : gamesErrorMessage
		const teamAbbr = getTeamAbbreviation(team?.teamName, team)

		return (
			<div css={styles.container}>
				<div css={styles.header}>
					<FontAwesomeIcon
						icon="arrow-left"
						css={styles.backArrow}
						onClick={handleBack}
					/>
					<Text size={16} bold css={styles.headerTitle}>
						{teamAbbr}: Schedule
					</Text>
				</div>
				<div css={styles.errorContainer}>
					<Text size={14} css={styles.errorText}>
						{displayError}
					</Text>
					{displayError.includes('does not participate') && (
						<Text size={12} css={styles.errorText} style={{ marginTop: '8px', opacity: 0.7 }}>
							This team may not be part of this tournament or may not have any scheduled games.
						</Text>
					)}
				</div>
			</div>
		)
	}

	const teamAbbr = getTeamAbbreviation(team?.teamName, team)
	const gamesByDate = getGamesByDate()
	const sortedDates = Object.keys(gamesByDate).sort((a, b) =>
		a.localeCompare(b)
	)

	return (
		<div css={styles.container}>
			<div css={styles.header}>
				<FontAwesomeIcon
					icon="arrow-left"
					css={styles.backArrow}
					onClick={handleBack}
				/>
				<Text size={16} bold css={styles.headerTitle}>
					{teamAbbr}: Schedule
				</Text>
			</div>

			<div css={styles.gamesContainer}>
				{sortedDates.length === 0 ? (
					<div css={styles.noGamesContainer}>
						<Text size={14} css={styles.noGamesText}>
							No scheduled games found for {teamAbbr}
						</Text>
						<Text size={12} css={styles.noGamesText} style={{ marginTop: '8px', opacity: 0.7 }}>
							This team may not have any upcoming games in the current tournament.
						</Text>
					</div>
				) : (
					sortedDates.map(dateKey => {
						const dateInfo = gamesByDate[dateKey]
						const today = new Date()
						const isToday = isSameDay(dateInfo.date, today)

						// Format date display to match games tab
						let dateDisplay
						if (isToday) {
							dateDisplay = `Today, ${format(dateInfo.date, 'MMMM d')}`
						} else {
							const isThisYear = dateInfo.date.getFullYear() === today.getFullYear()
							dateDisplay = isThisYear
								? format(dateInfo.date, 'MMMM d')
								: format(dateInfo.date, 'MMMM d, yyyy')
						}

						// Sort games by time within the date
						const sortedGames = dateInfo.games.sort((a, b) => {
							if (!a.startTime || !b.startTime) return 0
							return new Date(a.startTime) - new Date(b.startTime)
						})

						return (
							<div key={dateKey} css={styles.dateSection}>
								<Text size={16} bold css={styles.dateText}>
									{dateDisplay}
								</Text>

								{sortedGames.map((game, index) => (
									<GameCard
										key={game.id || `game-${dateKey}-${index}`}
										game={game}
									/>
								))}
							</div>
						)
					})
				)}
			</div>
		</div>
	)
}

TeamScheduleComponent.propTypes = {
	teamId: PropTypes.number.isRequired,
	team: PropTypes.shape({
		id: PropTypes.number,
		teamName: PropTypes.string,
		abbreviation: PropTypes.string,
		logo: PropTypes.string,
	}),
	loading: PropTypes.bool,
	error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	teamGames: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		startTime: PropTypes.string,
		teamA: PropTypes.string,
		teamB: PropTypes.string,
		teamOne: PropTypes.shape({
			logo: PropTypes.string,
			teamName: PropTypes.string,
		}),
		teamTwo: PropTypes.shape({
			logo: PropTypes.string,
			teamName: PropTypes.string,
		}),
	})),
	gamesLoading: PropTypes.bool,
	gamesError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
}

export default TeamScheduleComponent
